import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useContext, useEffect, useRef } from "react";
import { AppContext } from "../../../../context/AppContext";
import Swal from "sweetalert2";
import axios from "axios";
import GetToken from "../../../../GetToken";
import { handleInvalidToken } from "../../../../AuthUtils";
import AnimationSpinner from "../../../../AnimationSpinner";
import { toast } from "react-toastify"
import { motion } from "framer-motion";

import templateImage from "../../../../assets/images/templates/defaultTemplate.jpg";

// react icons import
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import { FaCheck } from "react-icons/fa6";
import { MdClose } from "react-icons/md";

// Material UI import
import Button from "@mui/material/Button";

// custom component import
import TalkingBookStepper from "../TalkingBookStepper";

// importing utility functions
import { truncateTextByChars } from "../../../../util";
import ErrorComponent from "../../../../pages/NoDataFound.jsx/NoError";
import { Empty } from "antd";
import EmptyIcon from "../../../../assets/images/error-image.jpg";

const BookIndex = ({ steps, activeStep, setActiveStep, bookToken }) => {
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [bookDetail, setBookDetail] = useState("");
	const [indexPages, setIndexPages] = useState([]);
	const { base_url } = useContext(AppContext);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	//model states 
	const [activeCard, setActiveCard] = useState(null);
	const [modalImage, setModalImage] = useState(null);
	const [isModelFullScreen, setIsModelFullScreen] = useState(false);
	const modalRef = useRef(null);

	const fetchPageData = async () => {
		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);
			const formData = new FormData();
			formData.append("action", "getBookIndexPages");
			formData.append("token", token);
			formData.append("book_token", bookToken);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				const fetchedIndexPages = response.data.items || [];
				const fetchedBookDetail = response.data.bookDetail;
				setIndexPages(fetchedIndexPages);
				setBookDetail(fetchedBookDetail);
				if (fetchedBookDetail.index_page !== null) {
					const temp = fetchedIndexPages.find(
						(index) => index.ids === fetchedBookDetail.index_page
					);
					setSelectedIndex(temp);
				}
				setTimeout(() => {
					setLoading(false);
				}, 1500);
			} else {
				setLoading(false);
				if (response.data.reset) {
					handleInvalidToken(navigate);
					return;
				}
				toast.warning(response.data.message);
				// Swal.fire({
				// 	icon: "warning",
				// 	title: "Warning",
				// 	text: response.data.message,
				// });
			}
		} catch (error) {
			setLoading(false);
			toast.error(error.response?.data?.message ||
				error.message ||
				"Something went wrong. Please try again.");
			// Swal.fire({
			// 	icon: "error",
			// 	title: "Error",
			// 	text:
			// 		error.response?.data?.message ||
			// 		error.message ||
			// 		"Something went wrong. Please try again.",
			// });
		}
	};

	useEffect(() => {
		fetchPageData();
	}, []);

	const handleChooseIndexPages = (indexPageIds) => {
		Swal.fire({
			title: "Confirm Selection",
			text: "Do you want to set these as your book's index pages?",
			icon: "question",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Yes, set as index pages!",
		}).then((result) => {
			if (result.isConfirmed) {
				saveBookIndexPages(indexPageIds);
			}
		});
	};

	const saveBookIndexPages = async (indexPageIds) => {
		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);

			const formData = new FormData();
			formData.append("action", "saveBookIndexPages");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("index_page_ids", indexPageIds);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				setLoading(false);
				toast.success("Book Index Page updated successfully!");
				fetchPageData();
			} else {
				setLoading(false);
				if (response.data.reset) {
					handleInvalidToken(navigate);
					return;
				}
				toast.warning(response.data.message);
				// Swal.fire({
				// 	icon: "warning",
				// 	title: "Warning",
				// 	text: response.data.message,
				// });
			}
		} catch (error) {
			setLoading(false);
			toast.error(error.response?.data?.message ||
				error.message ||
				"Something went wrong. Please try again.");
			// Swal.fire({
			// 	icon: "error",
			// 	title: "Error",
			// 	text:
			// 		error.response?.data?.message ||
			// 		error.message ||
			// 		"Something went wrong. Please try again.",
			// });
		}
	};

	const handleNext = () => {
		if (!bookDetail.index_page) {
			toast.error("Please Select Book Index Page");
			return;
		}
		if (indexPages.length > 0 && activeStep < steps.length - 1) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		}
	};
	const handleBack = () => {
		if (activeStep > 0) {
			setActiveStep((prevActiveStep) => prevActiveStep - 1);
		}
	};

	// model for preview index page implementation
	const modalVariants = {
		hidden: { opacity: 0, scale: 0.9 },
		visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
		exit: { opacity: 0, scale: 0.9, transition: { duration: 0.4 } }
	};
	const handleModelCloseClick = (event) => {
		if (event.target.classList.contains('history-image-model')) {
			closeModal();
		}
	};
	const openImageModal = (img) => {
		setModalImage(img);
		setIsModelFullScreen(true);
	};

	const closeModal = () => {
		setModalImage(null);
		setIsModelFullScreen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				closeModal();
			}
		};

		if (modalImage) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [modalImage]);

	return (
		<>
			{modalImage && (

				<div
					className="history-image-model shadow rounded position-absolute"
					ref={modalRef}
					onClick={handleModelCloseClick}
				>
					<motion.div 
						className="model-body position-relative"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 20 }}
						transition={{ duration: 0.3 }}
					>
						<motion.div 
							className=" d-flex justify-content-between mb-2 btn-container gap-2 "
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
						>
							<motion.button 
								className="btn btn-sm btn-primary" 
								style={{ zIndex: "1" }} 
								onClick={closeModal}
								whileHover={{ scale: 1.1 }}
								whileTap={{ scale: 0.9 }}
							>
								<MdClose />



							</motion.button>
						</motion.div>
						<motion.div 
							className="history-model-image-container"
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.4 }}
						>
							<img src={modalImage} alt="index page" className="img-fluid rounded w-100 h-100" />



						</motion.div>
					</motion.div>
				</div>
			)}
			<AnimationSpinner show={loading} />
			<div className="row align-items-center">
				<div className="col-lg-6 page-title-container">
					<h4 className="fw-semibold mb-1">
						{bookDetail.title
							? truncateTextByChars(bookDetail.title, 40)
							: "Loading..."}
					</h4>
					<p className="mb-1">Choose your preferred book index </p>
				</div>
				<div className="col-lg-6 justify-content-end">
					<div className="d-flex justify-content-end gap-4">
						<Button
							onClick={handleBack}
							variant="contained"
							color="secondary"
							title="Go Back"
							className="ms-2"
							sx={{
								minWidth: "35px",
								height: "38px",
								padding: "0 17px",
							}}
						>
							<IoIosArrowRoundBack
									size={30}
									fontWeight={600}
									color="white"
								/>
							Back
						</Button>
						<Button
							onClick={handleNext}
							variant="contained"
							color="secondary"
							title="Next step"
							className="ms-2"
							sx={{
								minWidth: "35px",
								height: "38px",
								padding: "0 17px",
							}}
						>
							Go to Next Step{" "}
							<span>
								<IoIosArrowRoundForward
									size={30}
									fontWeight={600}
									color="white"
								/>
							</span>
						</Button>
					</div>
				</div>
			</div>
			<div className="my-4">
				<TalkingBookStepper steps={steps} activeStep={activeStep} />
			</div>
			{indexPages.length > 0 ? (
			<div className="row mx-4">
				{indexPages.map((index, key) => (
					<div className="col-md-3 mb-3" key={key}>
						<div className={`index-card shadow-sm ${selectedIndex?.ids === index.ids ? "default-card" : ""}`}>
							{selectedIndex?.ids == index.ids ? (<div
								className="Selected_index"
							>
								<span>Selected</span>
							</div>) : null}
							<div className="index-img-container">
								<img
									src={!index.image ? templateImage : index.image}
									alt="Index Image"
									className="img-fluid mx-auto d-block"
								/>
								<div className="overlay d-flex flex-column justify-content-center align-items-center gap-2">
									{selectedIndex?.ids !== index.ids ? <div
										className="theme-btn pointer"
										onClick={() => handleChooseIndexPages(index.ids)}
									>
										<span>Choose Index</span>
									</div> : null}
									<div className="theme-btn pointer" onClick={() => openImageModal(!index.image ? templateImage : index.image)}>
										<span>Preview</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
			) : (
				 <ErrorComponent image={EmptyIcon} description={"No Index template available in page."} />
			)}
		</>
	);
};
export default BookIndex;
